import {
  Habit,
  RepeatingTask,
  Task,
  TaskFilterOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { filterHabits } from './filter-habits';
import { filterRepeatingTasks } from './filter-repeating-tasks';
import { filterTasksClientSide } from './filter-tasks-client-side';
import {
  isWithinDeadlineDeadlineWindow,
  isWithinDeadlineWindow,
} from './task-predicates';

// The server query enforces constraints `filterTasksClientSide` never re-applies
// (see packages/services/src/queries/get-tasks-query.ts): the `completedAt ==
// null` gate, the deadlineDeadline range and the scheduled-deadline range.
// Reproducing them here is what lets a client-side count equal the old
// server+client pipeline. Each range applies only when its bound is set — an
// unbounded window would wrongly drop null-dated tasks the server never
// filtered. (`tasksOnly` is handled by partitioning on type upstream.)
export const matchesTaskQueryConstraints = (
  filters: TaskFilterOptions,
  task: Task,
): boolean => {
  if (!filters.showCompleted && task.completedAt) {
    return false;
  }

  if (
    (filters.deadlineDeadlineStartDate !== undefined ||
      filters.deadlineDeadlineEndDate !== undefined) &&
    !isWithinDeadlineDeadlineWindow(task, {
      start: filters.deadlineDeadlineStartDate,
      end: filters.deadlineDeadlineEndDate,
    })
  ) {
    return false;
  }

  if (
    !filters.showCompleted &&
    (filters.deadlineStartDate !== undefined ||
      filters.deadlineEndDate !== undefined) &&
    !isWithinDeadlineWindow(task, {
      start: filters.deadlineStartDate,
      end: filters.deadlineEndDate,
    })
  ) {
    return false;
  }

  return true;
};

export type CountMatchingItemsOptions = {
  filters: TaskFilterOptions[];
  tasks: Task[];
  habits: Habit[];
  repeatingTasks: RepeatingTask[];
  weekStartsOn: WeekDays;
  today: Timestamp;
};

// How many items a filter document selects across all three buckets. Callers
// pass tasks already partitioned to `TaskType.Task`; habits and repeating tasks
// are counted through their own pipelines.
export const countMatchingItems = ({
  filters,
  tasks,
  habits,
  repeatingTasks,
  weekStartsOn,
  today,
}: CountMatchingItemsOptions): number => {
  if (!filters.length) {
    return 0;
  }

  const sourcedTasks = tasks.filter((task) =>
    filters.some((filter) => matchesTaskQueryConstraints(filter, task)),
  );

  return (
    filterTasksClientSide(sourcedTasks, filters).length +
    filterHabits({ habits, filters, weekStartsOn, today }).length +
    filterRepeatingTasks({ repeatingTasks, filters, weekStartsOn, today })
      .length
  );
};
